import type {
    CompRef,
    GenerateSingleImageParams,
    IdMap,
    ImagesInfoMap,
    Pointer,
    UpdateComponentBuilderImageParams,
    UpdateComponentSiteCreationImageParams,
    ImagesInfoMapFromServer,
    ImageInfo,
    GenerateImagesFromDescriptionsParams,
    IdMapValue,
    ImageDetails
} from '@wix/document-services-types'
import {
    getNextIdForPrefix,
    getSectionNameByPointer,
    ignoredTemplateOutlineTypes,
    type PointerPredicate
} from './aiExtensionContent'
import type {CreateExtArgs, EnvironmentContext} from '@wix/document-manager-core'
import type {StructureExtensionAPI} from '../structure'
import type {ComponentDefinitionExtensionAPI} from '../componentDefinition'
import {fetchImageUrlsForBuilder, fetchImageUrlsForSiteCreation} from './aiExtensionUtils'
import {DATA_TYPES} from '../../constants/constants'
import {handleAiError, predicateBuilderComp} from './aiBuilderUtils'
import type {ManifestExtensionAPI} from '../builderComponents/manifest'
import {createImageUploadConfig, processGeneratedImages, uploadImages} from './images/utils'
import _, {isEmpty} from 'lodash'
import type {LoggerExtAPI} from '../logger'
import {ERRORS, LOGS} from './constants'
import {ReportableError} from '@wix/document-manager-utils'
import type {RMApi} from '../rendererModel'
import {calculateImageMeasurements, createMediaMap} from './utils'

const caller = 'generateImages'

const DEFAULT_WIDTH = 1024
const DEFAULT_HEIGHT = 768

const compareImageIds = (a: string, b: string): number => {
    const matchA = /^image_(\d+)$/.exec(a)
    const matchB = /^image_(\d+)$/.exec(b)
    return (matchA ? Number(matchA[1]) : 0) - (matchB ? Number(matchB[1]) : 0)
}

export interface BuilderImageOutlineDataWithIdMapAndOldImageInfoMap {
    outline: BuilderImageOutlineData
    idMap: IdMap
    oldImagesInfo: ImagesInfoMap
}

type ImagePredicate = (pointer: Pointer) => boolean

interface BuilderImageSectionData {
    images: {id: string; width: string; height: string}[]
}

interface BuilderTextSectionData {
    [componentName: string]: Record<string, string>
}

type SectionData = BuilderImageSectionData & BuilderTextSectionData

export interface BuilderImageOutlineData {
    [section: string]: SectionData
}

interface ImageInjectionParams {
    compRefsWithHints: UpdateComponentBuilderImageParams['compRefsWithHints']
    onSuccess?: UpdateComponentBuilderImageParams['onSuccess']
    onError?: UpdateComponentBuilderImageParams['onError']
    signal?: AbortSignal
}

type FetchImageUrls = (
    outline: BuilderImageOutlineData,
    imagesWithHints: Record<string, string | undefined>,
    msid: string,
    signal?: AbortSignal
) => Promise<string>

const getBuilderImageOutlineData = (
    createExtArgs: CreateExtArgs,
    shouldAddImagePredicate: ImagePredicate,
    pagePointer: Pointer
): BuilderImageOutlineDataWithIdMapAndOldImageInfoMap => {
    const {pointers} = createExtArgs
    const {components} = createExtArgs.extensionAPI as StructureExtensionAPI
    const {componentDefinition} = createExtArgs.extensionAPI as ComponentDefinitionExtensionAPI
    const {manifest} = createExtArgs.extensionAPI as ManifestExtensionAPI

    const predicateBuilder = (pointer: Pointer): boolean => {
        const componentType = components.getComponentType(pointer)
        return predicateBuilderComp(createExtArgs.extensionAPI, componentType)
    }

    const getContentRecursively = (
        rootPointer: Pointer,
        shouldAddImage: ImagePredicate,
        idMap: IdMap,
        oldImagesInfo: Record<string, ImageInfo | null>,
        typeCounter: Record<string, number>,
        sectionName: string | undefined,
        predicate: PointerPredicate,
        outline: any
    ): void => {
        const type = components.getComponentType(rootPointer)
        const toAdd = shouldAddImage(rootPointer)

        if (ignoredTemplateOutlineTypes.has(type)) {
            return
        }

        if (componentDefinition.isContainer(type)) {
            if (componentDefinition.isSection(type)) {
                const sectionNameByPointer = getSectionNameByPointer(
                    createExtArgs.extensionAPI,
                    rootPointer,
                    DATA_TYPES.anchors
                )
                sectionName = getNextIdForPrefix(sectionNameByPointer, typeCounter)
            }
            const children = pointers.structure.getChildren(rootPointer)
            for (const childPointer of children) {
                getContentRecursively(
                    childPointer,
                    shouldAddImage,
                    idMap,
                    oldImagesInfo,
                    typeCounter,
                    sectionName,
                    predicate,
                    outline
                )
            }
        }
        if (!sectionName) return
        if (predicate(rootPointer)) {
            outline[sectionName] ??= {
                images: []
            }
            const textContextProps = manifest.getData(rootPointer, true, [{prop: 'text'}])
            textContextProps.forEach(context => {
                const {value: data} = context
                const mappedId = getNextIdForPrefix(context.type, typeCounter)
                return (outline[sectionName][mappedId] = data)
            })

            if (toAdd) {
                const mediaPropsInfo = createMediaMap(
                    createExtArgs.extensionAPI,
                    [rootPointer as CompRef],
                    'image',
                    caller,
                    typeCounter
                )
                const {
                    idMap: mediaIdMap,
                    originalData: mediaOriginalData,
                    mediaMeasurements: mediaMediaMeasurements
                } = mediaPropsInfo
                Object.assign(idMap, mediaIdMap)
                Object.entries(mediaOriginalData).forEach(([mappedId, data]) => {
                    if (!data) oldImagesInfo[mappedId] = null
                    else oldImagesInfo[mappedId] = {imageUri: data?.uri, width: data?.width, height: data?.height}
                })
                Object.keys(mediaIdMap).forEach(mappedId => {
                    const {width, height} = mediaMediaMeasurements[mappedId]
                    outline[sectionName].images.push({id: mappedId, width: String(width), height: String(height)})
                })
            }
        }
    }

    const outline: BuilderImageOutlineData = {}
    const typeCounter: Record<string, number> = {}
    const idMap = {}
    const oldImagesInfo = {}
    getContentRecursively(
        pagePointer,
        shouldAddImagePredicate,
        idMap,
        oldImagesInfo,
        typeCounter,
        undefined,
        predicateBuilder,
        outline
    )
    const outlineOfSectionsWithImages = Object.fromEntries(
        Object.entries(outline).filter(([, val]) => val.images.length > 0)
    )
    return {outline: outlineOfSectionsWithImages, idMap, oldImagesInfo}
}

export const getBuilderImageOutlineDataForInjection = (
    createExtArgs: CreateExtArgs,
    pointers: Pointer[]
): BuilderImageOutlineDataWithIdMapAndOldImageInfoMap => {
    const pagePointer = createExtArgs.pointers.structure.getPageOfComponent(pointers[0])
    const shouldAddImageInInjection =
        (targetPointers: Pointer[]): ImagePredicate =>
        (pointer: Pointer) =>
            targetPointers.some(targetPointer => pointer.id === targetPointer.id)
    return getBuilderImageOutlineData(createExtArgs, shouldAddImageInInjection(pointers), pagePointer)
}

const getImageInjection = async (
    {serverFacade}: EnvironmentContext,
    createExtArgs: CreateExtArgs,
    {compRefsWithHints, onSuccess, onError, signal}: ImageInjectionParams,
    currentCaller: string,
    fetchImageUrls: FetchImageUrls
): Promise<void> => {
    const {extensionAPI} = createExtArgs
    const {logger} = extensionAPI as LoggerExtAPI

    if (signal?.aborted) {
        logger.breadcrumb('image generation aborted', {
            tags: {caller: currentCaller},
            extras: {stage: 'entry'}
        })
        return
    }

    logger.interactionStarted(currentCaller, {tags: {caller: currentCaller}})

    const targetPointers: Pointer[] = compRefsWithHints.map(({compRef}) => compRef)

    const {outline, idMap, oldImagesInfo} = getBuilderImageOutlineDataForInjection(createExtArgs, targetPointers)
    const msid = createExtArgs.dal.get(createExtArgs.pointers.rendererModel.getMetaSiteId())

    const compIdToHints = _.fromPairs(compRefsWithHints.map(({compRef, hints}) => [compRef.id, [...hints]]))
    const defaultHint = compRefsWithHints[0]?.hints?.[0]
    const imagesWithHints = _.mapValues(idMap, value => compIdToHints[value.pointer.id]?.shift() ?? defaultHint)
    if (isEmpty(imagesWithHints)) {
        logger.captureError(
            new ReportableError({
                errorType: ERRORS.IMAGES.GENERATION_NO_IMAGES_WITH_HINTS,
                message: 'No images with hints found'
            }),
            {tags: {caller: currentCaller}}
        )
        onError?.({errors: ['No images with hints found']})
        return
    }

    try {
        logger.interactionStarted(LOGS.IMAGES.GENERATION, {
            tags: {caller: currentCaller},
            extras: {imagesAmount: Object.keys(idMap).length}
        })
        const res = await fetchImageUrls(outline, imagesWithHints, msid, signal)
        const imagesInfo: ImagesInfoMapFromServer = JSON.parse(res)
        logger.interactionEnded(LOGS.IMAGES.GENERATION, {tags: {caller: currentCaller}})

        if (signal?.aborted) {
            logger.breadcrumb('image generation aborted', {
                tags: {caller: currentCaller},
                extras: {stage: 'after-fetchImageUrls'}
            })
            return
        }

        await processGeneratedImages(
            createExtArgs,
            serverFacade,
            imagesInfo,
            idMap,
            oldImagesInfo,
            onSuccess,
            onError,
            currentCaller,
            signal
        )
    } catch (e: any) {
        if (signal?.aborted) {
            logger.breadcrumb('image generation aborted', {
                tags: {caller: currentCaller},
                extras: {stage: 'catch'}
            })
            return
        }
        handleAiError(e, ERRORS.IMAGES.GENERATION, logger, onError, {caller: currentCaller})
    }
}

export async function getImageInjectionForBuilder(
    {serverFacade}: EnvironmentContext,
    createExtArgs: CreateExtArgs,
    {
        compRefsWithHints,
        siteType,
        businessName,
        siteDescription,
        photoTheme,
        userRequestInfo,
        onSuccess,
        onError,
        signal
    }: UpdateComponentBuilderImageParams
): Promise<void> {
    return getImageInjection(
        {serverFacade},
        createExtArgs,
        {
            compRefsWithHints,
            onSuccess,
            onError,
            signal
        },
        caller,
        (outline, imagesWithHints, msid, sig) =>
            fetchImageUrlsForBuilder(
                serverFacade,
                outline,
                imagesWithHints,
                siteType,
                businessName,
                siteDescription,
                photoTheme,
                msid,
                userRequestInfo,
                sig
            )
    )
}

export async function getImageInjectionForSiteCreation(
    {serverFacade}: EnvironmentContext,
    createExtArgs: CreateExtArgs,
    {compRefsWithHints, userRequestInfo, onSuccess, onError, signal}: UpdateComponentSiteCreationImageParams
): Promise<void> {
    const scCaller = 'generateImagesSC'

    return getImageInjection(
        {serverFacade},
        createExtArgs,
        {
            compRefsWithHints,
            onSuccess,
            onError,
            signal
        },
        scCaller,
        (outline, imagesWithHints, msid, sig) =>
            fetchImageUrlsForSiteCreation(serverFacade, outline, imagesWithHints, msid, userRequestInfo, sig)
    )
}

export async function generateSingleImage(
    {serverFacade}: EnvironmentContext,
    createExtArgs: CreateExtArgs,
    {
        targetImage,
        siteType,
        businessName,
        siteDescription,
        photoTheme,
        userRequestInfo,
        onSuccess,
        onError
    }: GenerateSingleImageParams
): Promise<void> {
    const {extensionAPI} = createExtArgs
    const {manifest, logger} = extensionAPI as ManifestExtensionAPI & LoggerExtAPI
    const {compRef, innerElementPath, dataPath, prop, hint} = targetImage

    const singleImageCaller = 'generateSingleImage'
    logger.interactionStarted(singleImageCaller, {tags: {caller: singleImageCaller}})

    const sectionName = getSectionNameByPointer(createExtArgs.extensionAPI, compRef, DATA_TYPES.anchors)
    const mappedId = 'image'

    const {width, height} = calculateImageMeasurements(extensionAPI, compRef, innerElementPath)
    const outline = {
        [sectionName]: {
            images: [{id: mappedId, width: String(width), height: String(height)}]
        }
    } as BuilderImageOutlineData
    const idMap: IdMap = {
        [mappedId]: {
            pointer: compRef,
            prop,
            path: innerElementPath,
            nestedDataPath: dataPath
        }
    }

    const imageContextProps = manifest.getData(compRef, true, [{prop: 'image'}])
    const matchingContext = imageContextProps.find(
        context =>
            context.prop === prop &&
            _.isEqual(context.path ?? [], innerElementPath ?? []) &&
            _.isEqual(context.nestedDataPath?.map(path => path.key) ?? [], dataPath?.map(path => path.key) ?? [])
    )

    if (!matchingContext?.value) {
        throw new ReportableError({
            errorType: ERRORS.IMAGES.GENERATION,
            message: 'No matching image context for target path'
        })
    }

    const oldImagesInfo: ImagesInfoMap = {
        [mappedId]: {
            imageUri: matchingContext.value?.uri,
            width: matchingContext.value?.width,
            height: matchingContext.value?.height
        }
    }
    const imagesWithHints = {[mappedId]: hint}
    const msid = createExtArgs.dal.get(createExtArgs.pointers.rendererModel.getMetaSiteId())

    try {
        logger.interactionStarted(LOGS.IMAGES.GENERATION, {
            tags: {caller: singleImageCaller},
            extras: {imagesAmount: 1}
        })
        const res = await fetchImageUrlsForBuilder(
            serverFacade,
            outline,
            imagesWithHints,
            siteType,
            businessName,
            siteDescription,
            photoTheme,
            msid,
            userRequestInfo
        )
        const imagesInfo = JSON.parse(res)
        logger.interactionEnded(LOGS.IMAGES.GENERATION, {tags: {caller: singleImageCaller}})

        await processGeneratedImages(
            createExtArgs,
            serverFacade,
            imagesInfo,
            idMap,
            oldImagesInfo,
            onSuccess,
            onError,
            singleImageCaller
        )
    } catch (e: any) {
        handleAiError(e, ERRORS.IMAGES.GENERATION, logger, onError, {caller: singleImageCaller})
    }
}

export async function generateImagesFromDescriptions(
    {serverFacade}: EnvironmentContext,
    createExtArgs: CreateExtArgs,
    {
        imagesDetails,
        siteType,
        businessName,
        siteDescription,
        photoTheme,
        userRequestInfo,
        onSuccess,
        onError
    }: GenerateImagesFromDescriptionsParams
) {
    const {extensionAPI} = createExtArgs
    const {logger, rendererModel} = extensionAPI as LoggerExtAPI & RMApi
    const generateImagesFromDescriptionsCaller = 'generateImagesFromDescriptions'
    logger.interactionStarted(generateImagesFromDescriptionsCaller, {
        tags: {caller: generateImagesFromDescriptionsCaller}
    })
    const msid = rendererModel.getMetaSiteId()

    const imagesWithDescriptions: Record<string, string> = {}
    const idMap: IdMap = {}
    imagesDetails.forEach((imageDetails: ImageDetails, index: number) => {
        const id = `image_${index}`
        imagesWithDescriptions[id] = imageDetails.description
        idMap[id] = {
            pointer: undefined as unknown as Pointer,
            prop: id
        }
    })
    const syntheticOutline = {
        section: {
            images: imagesDetails.map((imageDetails: ImageDetails, index: number) => ({
                id: `image_${index}`,
                width: String(imageDetails.width ?? DEFAULT_WIDTH),
                height: String(imageDetails.height ?? DEFAULT_HEIGHT)
            }))
        }
    } as unknown as BuilderImageOutlineData

    try {
        logger.interactionStarted(LOGS.IMAGES.GENERATION, {
            tags: {caller: generateImagesFromDescriptionsCaller},
            extras: {imagesAmount: imagesDetails.length}
        })
        const res = await fetchImageUrlsForBuilder(
            serverFacade,
            syntheticOutline,
            imagesWithDescriptions,
            siteType,
            businessName,
            siteDescription,
            photoTheme,
            msid,
            userRequestInfo
        )
        const imagesInfoFromServer = JSON.parse(res) as ImagesInfoMapFromServer
        const imagesInfo = Object.fromEntries(
            Object.entries(imagesInfoFromServer).filter(
                (entry): entry is [string, ImageInfo] =>
                    entry[1] !== null && typeof entry[1].imageUri === 'string' && entry[1].imageUri.length > 0
            )
        ) as ImagesInfoMap
        logger.interactionEnded(LOGS.IMAGES.GENERATION, {tags: {caller: generateImagesFromDescriptionsCaller}})

        if (isEmpty(imagesInfo)) {
            const reportableError = new ReportableError({
                errorType: ERRORS.IMAGES.GENERATION,
                message: 'Images generation returned an empty object'
            })
            logger.captureError(reportableError, {tags: {caller: generateImagesFromDescriptionsCaller}})
            onError?.({errors: ['No images generated']})
            return
        }

        const imagesById: Record<string, ImageInfo> = {}
        const config = {
            ...createImageUploadConfig(logger, generateImagesFromDescriptionsCaller),
            injectItem: (_createExtArgs: CreateExtArgs, idMapValue: IdMapValue, imageInfo: ImageInfo | null) => {
                if (!imageInfo || !idMapValue?.prop) {
                    return
                }
                imagesById[idMapValue.prop] = imageInfo
            }
        }

        const onSuccessWithImages = (...args: any[]) => {
            const images = Object.keys(imagesById)
                .sort(compareImageIds)
                .map(id => imagesById[id])
            onSuccess?.(images, args)
        }

        await uploadImages(
            createExtArgs,
            serverFacade,
            imagesInfo,
            idMap,
            {},
            [],
            onSuccessWithImages,
            onError,
            generateImagesFromDescriptionsCaller,
            config
        )
    } catch (e: any) {
        handleAiError(e, ERRORS.IMAGES.GENERATION, logger, onError, {caller: generateImagesFromDescriptionsCaller})
    }
}
